// store/locations.js
// New module — full CRUD for locations, product assignments and user assignments

import axios from 'axios';
import router from '../router';

// ---------------------------------------------------------------
// Helpers — avoids repeating baseURL and token in every action
// ---------------------------------------------------------------
function apiBase() {
  return process.env.VUE_APP_API_URL || 'http://127.0.0.1:3000/api';
}

function authHeaders() {
  const token = localStorage.getItem('userToken');
  if (!token) {
    router.push({ name: 'login' });
    return null;
  }
  return { Authorization: `Bearer ${token}` };
}

const state = {
  locations:        [],   // all locations for this customer
  currentLocation:  null, // selected location (for filtering)
  locationProducts: [],   // products assigned to selected location
  locationUsers:    [],   // users assigned to selected location
};

const mutations = {
  SET_LOCATIONS(state, locations) {
    state.locations = locations;
  },
  SET_CURRENT_LOCATION(state, location) {
    state.currentLocation = location;
    // Persist so page refresh keeps the selection
    if (location) {
      localStorage.setItem('currentLocationId', location.locationId);
    } else {
      localStorage.removeItem('currentLocationId');
    }
  },
  SET_LOCATION_PRODUCTS(state, products) {
    state.locationProducts = products;
  },
  SET_LOCATION_USERS(state, users) {
    state.locationUsers = users;
  },
};

const actions = {

  // ---------------------------------------------------------------
  // LOCATIONS CRUD
  // ---------------------------------------------------------------

  async fetchLocations({ commit }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(`${apiBase()}/locations`, { headers });
      commit('SET_LOCATIONS', response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  },

  async createLocation({ dispatch }, locationData) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.post(`${apiBase()}/locations`, locationData, { headers });
      await dispatch('fetchLocations');
    } catch (error) {
      console.error('Error creating location:', error);
      throw error;
    }
  },

  async updateLocation({ dispatch }, locationData) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.put(`${apiBase()}/locations/${locationData.locationId}`, locationData, { headers });
      await dispatch('fetchLocations');
    } catch (error) {
      console.error('Error updating location:', error);
      throw error;
    }
  },

  async deleteLocation({ dispatch }, locationId) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${apiBase()}/locations/${locationId}`, { headers });
      await dispatch('fetchLocations');
    } catch (error) {
      console.error('Error deleting location:', error);
      throw error;
    }
  },

  // ---------------------------------------------------------------
  // PRODUCT ASSIGNMENTS
  // ---------------------------------------------------------------

  async fetchLocationProducts({ commit }, locationId) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(`${apiBase()}/locations/${locationId}/products`, { headers });
      commit('SET_LOCATION_PRODUCTS', response.data);
    } catch (error) {
      console.error('Error fetching location products:', error);
      throw error;
    }
  },

  async assignProducts({ dispatch }, { locationId, productIds }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.post(
        `${apiBase()}/locations/${locationId}/products`,
        { productIds },
        { headers }
      );
      await dispatch('fetchLocationProducts', locationId);
    } catch (error) {
      console.error('Error assigning products:', error);
      throw error;
    }
  },

  async removeProduct({ dispatch }, { locationId, productId }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.delete(
        `${apiBase()}/locations/${locationId}/products/${productId}`,
        { headers }
      );
      await dispatch('fetchLocationProducts', locationId);
    } catch (error) {
      console.error('Error removing product from location:', error);
      throw error;
    }
  },

  // ---------------------------------------------------------------
  // USER ASSIGNMENTS
  // ---------------------------------------------------------------

  async fetchLocationUsers({ commit }, locationId) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      const response = await axios.get(`${apiBase()}/locations/${locationId}/users`, { headers });
      commit('SET_LOCATION_USERS', response.data);
    } catch (error) {
      console.error('Error fetching location users:', error);
      throw error;
    }
  },

  async assignUsers({ dispatch }, { locationId, userIds }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.post(
        `${apiBase()}/locations/${locationId}/users`,
        { userIds },
        { headers }
      );
      await dispatch('fetchLocationUsers', locationId);
    } catch (error) {
      console.error('Error assigning users to location:', error);
      throw error;
    }
  },

  async removeUser({ dispatch }, { locationId, userId }) {
    const headers = authHeaders();
    if (!headers) return;
    try {
      await axios.delete(
        `${apiBase()}/locations/${locationId}/users/${userId}`,
        { headers }
      );
      await dispatch('fetchLocationUsers', locationId);
    } catch (error) {
      console.error('Error removing user from location:', error);
      throw error;
    }
  },

  // ---------------------------------------------------------------
  // Set currently selected location (for filtering)
  // ---------------------------------------------------------------
  setCurrentLocation({ commit }, location) {
    commit('SET_CURRENT_LOCATION', location);
  },
};

const getters = {
  locations:          state => state.locations,
  currentLocation:    state => state.currentLocation,
  locationProducts:   state => state.locationProducts,
  locationUsers:      state => state.locationUsers,
  locationById: state => id => state.locations.find(l => l.locationId === id),
  // locationIds as a flat array — useful for filtering
  locationIds: state => state.locations.map(l => l.locationId),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};